/**
 * Loader for the `.dcs/pages.yaml` route manifest.
 *
 * `pages.yaml` is the canonical page registry maintained by the DCS portal and
 * by Copilot when scaffolding pages. For the build-time SEO emitter we only
 * need each route's `slug` and `path` (e.g. `{ slug: 'home', path: '/' }`).
 *
 * The parser is intentionally defensive: any missing/unparseable file or
 * malformed entry yields `null` (caller logs + no-ops) so a bad manifest can
 * never break a production build.
 */

import fs from 'node:fs'
import path from 'node:path'
import yaml from 'js-yaml'
import { matchesExcludedGlob } from './sitemap'

/** A single route extracted from `.dcs/pages.yaml`. */
export interface PageRouteEntry {
  /** Page slug, matching an entry in `seo.yaml` `pages.<slug>` (may be absent). */
  slug: string
  /** Route path, e.g. `/`, `/services`, `/blog/my-post`. */
  path: string
  /**
   * Human title from the manifest (e.g. "Kitchen Cabinet Refresh"). Used as the
   * per-route title fallback when `seo.yaml` has no entry for this page, so
   * un-configured routes (e.g. blog posts) get unique titles. Optional.
   */
  title?: string
  /**
   * Per-page last-modification date for the sitemap `<lastmod>`. A W3C-datetime
   * string authored in `pages.yaml` (`lastmod` / `lastUpdated` / `dateModified`).
   * Validated + de-fabricated at emit time; an invalid or absent value falls
   * back to the site-wide lastmod. Never invented.
   */
  lastmod?: string
}

/** Shape of the relevant slice of `.dcs/pages.yaml`. */
interface RawPagesManifest {
  pages?: Array<{
    slug?: unknown
    path?: unknown
    title?: unknown
    lastmod?: unknown
    lastUpdated?: unknown
    dateModified?: unknown
  }>
}

/**
 * Resolve and parse `.dcs/pages.yaml` from one of the usual locations.
 *
 * Mirrors the `.dcs` path resolution used elsewhere in the package (project
 * root, parent dir for VitePress-style nesting, and cwd).
 *
 * @returns the list of `{ slug, path }` routes, or `null` if the file is
 *   absent, unreadable, unparseable, or contains no usable page entries.
 */
export function loadPagesManifest(
  projectRoot: string,
  relativePagesPath: string,
  debug = false
): PageRouteEntry[] | null {
  const possiblePaths = [
    path.resolve(projectRoot, relativePagesPath),
    path.resolve(projectRoot, '..', relativePagesPath),
    path.resolve(process.cwd(), relativePagesPath),
  ]

  let foundPath: string | undefined
  for (const testPath of possiblePaths) {
    if (fs.existsSync(testPath)) {
      foundPath = testPath
      break
    }
  }

  if (!foundPath) {
    if (debug) {
      console.warn('[dcs-seo] No pages.yaml found at:')
      possiblePaths.forEach((p) => console.warn(`  - ${p}`))
    }
    return null
  }

  let raw: RawPagesManifest
  try {
    raw = yaml.load(fs.readFileSync(foundPath, 'utf8')) as RawPagesManifest
  } catch (error) {
    console.warn(`[dcs-seo] Failed to parse ${foundPath}:`, error)
    return null
  }

  const entries = parsePagesManifest(raw)
  if (!entries) {
    console.warn(`[dcs-seo] pages.yaml at ${foundPath} has no usable page entries`)
    return null
  }

  if (debug) {
    console.log(`[dcs-seo] Loaded ${entries.length} routes from ${foundPath}`)
  }
  return entries
}

/**
 * Extract `{ slug, path }` routes from an already-parsed manifest object.
 * Exposed separately so tests can exercise it without touching the filesystem.
 *
 * Entries missing a string `path` are skipped; a missing slug falls back to the
 * empty string (so the route still gets baked, using global SEO defaults).
 */
export function parsePagesManifest(raw: unknown): PageRouteEntry[] | null {
  if (!raw || typeof raw !== 'object') return null
  const pages = (raw as RawPagesManifest).pages
  if (!Array.isArray(pages)) return null

  const routes: PageRouteEntry[] = []
  for (const entry of pages) {
    if (!entry || typeof entry !== 'object') continue
    const p = (entry as { path?: unknown }).path
    if (typeof p !== 'string' || p.length === 0) continue
    const slug = (entry as { slug?: unknown }).slug
    const title = (entry as { title?: unknown }).title
    const lastmodRaw =
      (entry as { lastmod?: unknown }).lastmod ??
      (entry as { lastUpdated?: unknown }).lastUpdated ??
      (entry as { dateModified?: unknown }).dateModified
    const lastmod =
      typeof lastmodRaw === 'string' && lastmodRaw.length > 0 ? lastmodRaw : undefined
    routes.push({
      slug: typeof slug === 'string' ? slug : '',
      path: p,
      ...(typeof title === 'string' && title.length > 0 ? { title } : {}),
      ...(lastmod ? { lastmod } : {}),
    })
  }

  return routes.length > 0 ? routes : null
}

// =============================================================================
// Which manifest routes the build actually EMITS (C-361 F5)
// =============================================================================

/**
 * The two exclusion inputs the per-route HTML emitter honours.
 *
 * `exclude` is a plugin OPTION (paths or slugs); `excludedGlobs` is the
 * `pages.yaml` top-level `excluded:` list. Neither is a file the runtime can
 * read, which is exactly why the RUNTIME MANIFEST has to be filtered before it
 * is injected rather than filtered again at lookup time.
 */
export interface RouteEmissionRules {
  /** `dcsSeoPlugin({ exclude })` — exact route paths OR slugs. */
  exclude?: readonly string[]
  /** `pages.yaml` top-level `excluded:` globs, e.g. `/dev-*`, `/_*`. */
  excludedGlobs?: readonly string[]
}

/** Why a manifest route is NOT emitted, or `null` when it is. */
export type RouteExclusionReason = 'excluded' | 'excluded-glob'

/**
 * THE SINGLE EMISSION PREDICATE. Both the per-route HTML emitter and the
 * `__DCS_PAGES__` runtime manifest resolve "is this route emitted?" through
 * THIS function.
 *
 * WHY IT EXISTS (C-361 F5, verified). The emitter skipped `exclude` entries and
 * `excluded:` globs, while `__DCS_PAGES__` was built from the raw manifest and
 * carried EVERY loaded route. A private `/dev-*` route was therefore never baked
 * — correct — but `installSeoHead` would still runtime-assert a canonical and
 * `index, follow` over it on the first in-app navigation. The manifest has to
 * model what the emitter WRITES, not what `pages.yaml` DECLARES, and the only
 * way that survives a future edit is for there to be one predicate rather than
 * two implementations that agree today.
 */
export function routeExclusionReason(
  route: Pick<PageRouteEntry, 'slug' | 'path'>,
  rules: RouteEmissionRules
): RouteExclusionReason | null {
  const exclude = rules.exclude ?? []
  if (exclude.includes(route.path)) return 'excluded'
  if (route.slug && exclude.includes(route.slug)) return 'excluded'
  if (matchesExcludedGlob(route.path, [...(rules.excludedGlobs ?? [])])) return 'excluded-glob'
  return null
}

/** `true` when the build emits a per-route `<head>` for this route. */
export function isRouteEmitted(
  route: Pick<PageRouteEntry, 'slug' | 'path'>,
  rules: RouteEmissionRules
): boolean {
  return routeExclusionReason(route, rules) === null
}
